import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Logo from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

const CATEGORIES = [
  {
    key: "about",
    label: "About eSulah",
    faqs: [
      {
        q: "What is eSulah?",
        a: "eSulah is an Online Dispute Resolution (ODR) platform. We use technology to help individuals and businesses resolve their legal disputes — like pending payments, consumer complaints, or breached contracts — completely online. We connect you with a nationwide panel of certified, independent experts (Mediators and Arbitrators) to reach a fair settlement without ever needing to visit a courtroom.",
      },
      {
        q: "Are the settlements reached on eSulah legally binding?",
        a: "Yes. Any settlement agreement or arbitral award reached through eSulah is legally binding and fully enforceable in Indian courts, in accordance with the Arbitration and Conciliation Act, 1996.",
      },
      {
        q: "Is eSulah a law firm?",
        a: "No, eSulah is a technology platform. We do not provide legal advice or act as your lawyer. We facilitate the resolution process by providing a secure online environment and connecting you with independent, certified mediators and arbitrators who guide the dispute resolution process.",
      },
      {
        q: "Do I need a lawyer to use eSulah?",
        a: "No, you do not need a lawyer to file a case or participate in mediation or arbitration on eSulah. Our platform and processes are designed to be user-friendly and accessible to everyone. You are of course entirely free to consult or hire a legal representative if you prefer.",
      },
      {
        q: "How secure is my data and evidence on the platform?",
        a: "Your privacy is our priority. eSulah uses secure, encrypted servers hosted in India. All documents uploaded and communications held during the resolution process are kept strictly confidential.",
      },
    ],
  },
  {
    key: "individuals",
    label: "For individuals",
    faqs: [
      {
        q: "What kinds of disputes can I file on eSulah?",
        a: "Individuals can use eSulah to resolve a wide variety of civil and commercial disputes — property issues, family disputes (non-criminal), consumer grievances, tenancy issues, and personal loan defaults.",
      },
      {
        q: "What happens if the other party refuses to join the process?",
        a: "Online Dispute Resolution — particularly Mediation — requires the mutual consent of both parties. Once you file a case, our team will reach out to the other party to invite them to participate. If they decline or do not respond, the case will be closed, and you are free to pursue traditional legal remedies.",
      },
      {
        q: "How long does it take to resolve a dispute on eSulah?",
        a: "While traditional court cases can take years, disputes on eSulah are typically resolved much faster. Depending on the complexity of the case and the responsiveness of both parties, the average resolution time is around 45 days.",
      },
      {
        q: "How much does it cost?",
        a: "We believe justice should be affordable. eSulah offers transparent, flat-fee pricing depending on the claim amount and the type of resolution service (Mediation or Arbitration). Reach out to us for the current fee schedule.",
      },
    ],
  },
  {
    key: "businesses",
    label: "For businesses",
    faqs: [
      {
        q: "How can eSulah help my business?",
        a: "eSulah helps enterprises, MSMEs and quick-commerce platforms recover pending dues and resolve high-volume consumer disputes rapidly. Businesses save significant time, reduce legal costs by up to 80%, and preserve valuable customer and vendor relationships by avoiding hostile litigation.",
      },
      {
        q: "Can we file cases in bulk?",
        a: "Yes. For businesses with high volumes of disputes (banks, NBFCs, e-commerce platforms), we offer seamless bulk-filing capabilities and dedicated enterprise dashboards to track all cases in real time.",
      },
      {
        q: "Who are the Arbitrators and Mediators on your panel?",
        a: "Our panel of mediators and arbitrators consists of highly qualified, independent professionals from across India — including retired judges, seasoned lawyers, and certified mediators. They are carefully vetted for their subject-matter expertise and their commitment to neutrality.",
      },
    ],
  },
  {
    key: "professionals",
    label: "For professionals",
    faqs: [
      {
        q: "How can I become an eSulah Resolutionary?",
        a: "Becoming an eSulah Resolutionary — whether as an Arbitrator, Mediator or Case Manager — is a structured journey designed to ensure the highest standards of empathy and fairness. The path involves completing our comprehensive eSulah ODR certification course, followed by a 1:1 evaluation session with a behavioural psychologist.",
      },
      {
        q: "Is there a registration fee for the eSulah ODR course?",
        a: "Yes. To maintain the quality of training, interactive modules and certification, there is a nominal course fee (inclusive of all digital training materials). Contact us for the current fee.",
      },
      {
        q: "Who can become a Case Manager?",
        role: "Case Manager",
        blurb: "The backbone of the online dispute-resolution process. You act as the bridge between the disputing parties — helping them coordinate seamlessly, facilitating communication, and ensuring the process moves forward without technical or administrative friction.",
        sections: [
          { h: "Ideal background", b: "Final-year graduation students or recent graduates looking for a stepping stone into modern legal-tech and administrative conflict resolution. Degrees in Law, B.Com, Psychology or Finance are highly preferred." },
          { h: "Key skills",        b: "Comfort with MS Excel, decent email writing skills, and a strong command of English along with at least one regional Indian language (Hindi, Tamil, Bengali, and so on) to effectively communicate with diverse parties." },
          { h: "Nature of the role", b: "Flexible, part-time — suited to fit alongside final-year studies or early career exploration. Exceptional Case Managers often transition into full-time operational roles across departments at eSulah." },
        ],
      },
      {
        q: "Will someone guide me through the platform? How does remuneration work?",
        a: "You won't be left to figure things out alone. After successfully completing the course, a dedicated ODR Coach will be assigned to support and handhold you through your very first 10 cases on the platform. Remuneration is competitive and varies depending on the type, complexity and financial value of the dispute you are handling.",
      },
      {
        q: "Who can become an eSulah Mediator?",
        role: "Mediator",
        blurb: "An independent, empathetic, neutral professional who facilitates constructive dialogue between disputing parties. Your goal is not to force a judgment — it is to gently guide both sides to find a mutually agreeable, peaceful settlement (sulah).",
        sections: [
          { h: "Roles & responsibilities", b: "Facilitate open and calm communication between parties in a completely neutral manner. Conduct digital mediation sessions through the eSulah platform. Help both sides identify the root issues and creatively explore settlement options. Assist in drafting a formal settlement agreement once a consensus is reached. Uphold absolute confidentiality, neutrality and procedural fairness at all times." },
          { h: "Qualifications required", b: "Minimum of 4 years of professional experience. Solid background in law, finance, banking, accounting, management or corporate operations. Strong natural negotiation, active-listening and conflict-resolution skills. Total comfort with handling virtual meetings and digital platforms." },
          { h: "Onboarding process", b: "1) Learn — register for and complete the self-paced eSulah ODR certification course. 2) Evaluate — pass a 1:1 interview with a behavioural psychologist; on positive feedback and credential verification your neutral profile is verified. 3) Practice — first 10 mediation cases under the direct guidance of an ODR Coach, then graduate to independent case assignments." },
        ],
      },
      {
        q: "Who can become an eSulah Arbitrator?",
        role: "Arbitrator",
        blurb: "An Arbitrator acts as a private judge in the digital space. Unlike a mediator, an arbitrator reviews evidence, hears legal arguments from both sides, and passes a formal, legally binding decision — an Arbitral Award — which holds the same weight as a traditional court decree under Indian law.",
        sections: [
          { h: "Roles & responsibilities", b: "Review case pleadings, contracts and digital evidence submitted via the eSulah dashboard. Conduct formal, structured online arbitration hearings with both parties and their legal counsels. Apply the Arbitration and Conciliation Act, 1996 with strict adherence to natural justice. Evaluate the merits of the dispute objectively without bias or external influence. Draft and execute clear, legally sound and enforceable Arbitral Awards." },
          { h: "Qualifications required", b: "7–10 years of professional experience, with heavy preference for seasoned advocates, retired judicial officers, corporate legal heads, or senior domain experts (chartered accountants, senior engineers for technical disputes). A valid degree in law (LLB / LLM) or deep, verifiable industry specialization. Impeccable legal analytical skills and expertise in drafting structured legal orders." },
          { h: "Onboarding process", b: "1) Certify — complete the specialized eSulah advanced ODR modules tailored for arbitral procedures. 2) Verify — comprehensive background and credentials check by our legal empanelment board, followed by a behavioural compatibility assessment. 3) Empanel — get listed on our elite panel of mediators & arbitrators. Cases matching your specific domain expertise are assigned per platform algorithms and party selection." },
        ],
      },
    ],
  },
];

export default function Faqs() {
  const [active, setActive] = useState("about");
  const cur = CATEGORIES.find(c => c.key === active) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-sand">
      <header className="border-b hairline bg-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-[13px] text-ink/65 inline-flex items-center hover:text-ink"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link>
        </div>
      </header>

      <section className="bg-court text-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-2"><div className="eyebrow-light">Common questions</div></div>
          <div className="lg:col-span-7">
            <h1 className="font-display text-[38px] lg:text-[56px] balanced" style={{ fontWeight: 400, lineHeight: 1.02 }}>
              How eSulah works — in plain answers.
            </h1>
          </div>
          <div className="lg:col-span-3 text-sand/75 text-[14px]">
            Pick a category, or ask us anything directly at <a href="mailto:support@e-sulah.com" className="text-clay underline-offset-4 hover:underline">support@e-sulah.com</a>.
          </div>
        </div>
      </section>

      <main className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3">
          <div className="eyebrow mb-4">Categories</div>
          <ul className="border-t hairline" data-testid="faq-categories">
            {CATEGORIES.map((c) => (
              <li key={c.key} className="border-b border-stone/40">
                <button
                  onClick={() => setActive(c.key)}
                  data-testid={`faq-cat-${c.key}`}
                  className={`w-full text-left py-4 flex items-center justify-between transition-colors ${active === c.key ? "text-clay" : "text-ink/80 hover:text-ink"}`}
                >
                  <span className="font-display text-[18px]" style={{ fontWeight: 400 }}>{c.label}</span>
                  <span className="font-mono text-[10px] tracking-[0.14em]">{String(c.faqs.length).padStart(2, "0")}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="lg:col-span-9">
          <div className="eyebrow mb-4">{cur.label}</div>
          <Accordion type="single" collapsible className="border-t hairline" data-testid="faqs-accordion">
            {cur.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-stone/40 last:border-0">
                <AccordionTrigger data-testid={`faq-trigger-${cur.key}-${i}`} className="text-left font-display text-[18px] lg:text-[20px] text-court hover:no-underline py-5" style={{ fontWeight: 400 }}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/80 leading-relaxed text-[15px] pb-6">
                  {f.a && <p>{f.a}</p>}
                  {f.blurb && <p className="mb-4">{f.blurb}</p>}
                  {f.sections && (
                    <div className="mt-2 border-l-2 border-clay pl-4 space-y-4">
                      {f.sections.map((s) => (
                        <div key={s.h}>
                          <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-clay mb-1">{s.h}</div>
                          <p className="text-ink/80 text-[14px]">{s.b}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 border-t hairline pt-8">
            <div className="eyebrow mb-3">Still curious?</div>
            <h3 className="font-display text-[24px] text-court" style={{ fontWeight: 400 }}>Talk to a human.</h3>
            <p className="text-ink/70 text-[14px] mt-2 max-w-lg">If your question isn't answered above, we'd love to hear from you. Write to <a href="mailto:support@e-sulah.com" className="text-clay underline-offset-4 hover:underline">support@e-sulah.com</a> or <Link to="/#contact" className="text-clay underline-offset-4 hover:underline">use our contact form</Link>.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
